/*================================================================
‘Copyright © 2020, Cheers Interactive Pvt Ltd.  All rights reserved.
     File Description : Trend taxonomy Tree Component
     Require Props :
        handleSave : Save Tree Data
        loadOtherTagInfo : Switch between trend and tree tagging
        saveTreeData : Save Technology taxonomy data
        contentId : Content Id [news id | company id]
        fetchContentData : Fetch content taxonomy
 --------------------------------------------------------------------------------- 
    Creation Details 
    Date Created				: 20/Aug/2020 
    Author						: Aditya Tijare
================================================================ 
*/
import "../index.scss";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { fetchTaxonomyTrend, getObjectiveSgfTag, fetchContentTags, addContentTags } from "../../../../middleware/services/cmsApi";
import noTaxonomyImage from "../../../../assets/images/no-taxonomy.svg";
import { showLoader, hideLoader, actionError, showMessage } from "../../../../middleware/actions/utilityAction";
import { showAlertBox } from "../../../../middleware/actions/alertBoxAction";
import PropTypes from "prop-types";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import SortableTree from "../sortableTree";
import { Modal } from "react-bootstrap";
import { MultiSelect } from "../../../components/CustomFormElements/syncFusionFormFields";
import SideBar from "./SideBar";

const Taxonomy = (props) => {
  const dispatch = useDispatch();
  const [tagging, setTagging] = useState([]);
  const [technologyData, setTechnologyData] = useState([]);
  const [selectedTechnology, setSelectedTechnology] = useState(undefined);
  const [taxonomy, setTaxonomy] = useState([]);
  const [checked, setChecked] = useState([]);
  const [contentTaggedIds, setContentTaggedIds] = useState([]);
  const [industry, setIndustry] = useState([]);
  const [reRender, setReRender] = useState(false);
  const [sgfName, setSgfName] = useState(undefined);
  const [selectedSgfIndustry, setSelectedSgfIndustry] = useState("");
  const [contentTaggingData, setContentTaggingData] = useState([]);
  let taggingData = [];
  const [sgfTrendData, setSgfTrendData] = useState([]);
  const [leftTreeCollapse, setLeftTreeCollapse] = useState(true);
  const [rightTreeCollapse, setRightTreeCollapse] = useState(true);
  const [showModalAdd, setShowModalAdd] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [contentTagsDataSource, setContentTagsDataSource] = useState([]);
  const [tagsLength, setTagsLength] = useState(0);
  const [show, setShow] = useState(false);
  const [rightTreeCheckedSequence, setRightTreeCheckedSequence] = useState([]);
  const [rightTreeCheckedSequenceTag, setRightTreeCheckedSequenceTag] = useState([]);
  const [sequenceCount, setSequenceCount] = useState(0);
  const defaultTrend = { trendId: "0", trendName: "All", trendIsDisabled: "NO", trendIsHidden: "YES" };

  const formik = useFormik({
    initialValues: {
      contentTag: [],
    },
  });

  /*
        @Description : Use only for avoiding trend change mechanism on save trend
    */
  const [leftTreeChecked, setLeftTreeChecked] = useState([]);
  const [rightTreeChecked, setRightTreeChecked] = useState([]);
  const [sgfData, setSgfData] = useState({
    trend_id: "",
    trend_name: "",
    trend_parent_id: "",
    trend_is_sgf: 1,
    trend_sub_root: "",
    trend_root: "",
  });

  const fetchIndustry = () => {
    dispatch(showLoader());
    fetchTaxonomyTrend({
      fields: ["id", "trendName", "trendParentId", "trendParentName"],
      filters: [["trendParentId", "eq", 0]],
      limit: import.meta.env.VITE_TAXONOMY_LIMIT || 10000,
    })
      .then((res) => {
        setSelectedSgfIndustry(res.data.data[0].id);
        setIndustry(res.data.data);
        fetchTrend(res.data.data[0].id);
        // setSgfData({
        //     ...sgfData,
        //     trend_parent_id: res.data.data[0].id,
        //     trend_root : res.data.data[0].id
        // })
      })
      .catch((err) => {
        console.error("err");
      });
  };
  /*
       @Description : Fetch taxonomy tagging for respective content 
       1. fetchContentTagging received from parent component as a props
   */
  const fetchContentTagging = (isTemp) => {
    dispatch(showLoader());
    const contentTaxonomy = props.fetchContentTagging(props.contentId, !isTemp);
    contentTaxonomy
      .then((res) => {
        let { data = [] } = res.data;

        data = [...new Map(data.map((v) => [v.trendId, v])).values()];
        if (data.length) {
          setContentTaggingData(data);
          taggingData = data.map((values) => values.trendId);
        }
        const selectedNodeData = buildSelectedTreeJson(data);
        const selectedSgfNodeData = buildSelectedSgfTreeJson(JSON.parse(JSON.stringify(selectedNodeData)));
        setTagging(
          selectedSgfNodeData.map((item) => {
            item["level"] = 2;
            return item;
          })
        );
        setContentTaggedIds(taggingData);
        setRightTreeCollapse(true);
        setRightTreeChecked([]);
        setReRender(true);
        if (industry?.length) dispatch(hideLoader());
      })
      .catch((err) => {
        dispatch(hideLoader());
        console.error("err fetchProductTaxonomy :: ", err);
      });
  };

  const fetchSgfTrend = (sgfName) => {
    getObjectiveSgfTag({ filters: [["sgfName", "eq", encodeURIComponent(sgfName)]], limit: 2000 })
      .then((res) => {})
      .catch((err) => {
        dispatch(hideLoader());
        console.error("err fetchTrend :: ", err);
      });
  };
  /*
        @Description : Fetch all trend 
    */
  const fetchTrend = (selectedIndustry) => {
    dispatch(showLoader());
    getObjectiveSgfTag({
      filters: [["sector.sectorId", "eq", selectedIndustry]],
      sort: "trendName:asc",
      limit: import.meta.env.VITE_TAXONOMY_LIMIT || 10000,
    })
      .then((res) => {
        dispatch(hideLoader());
        setSgfTrendData(
          res.data.data.filter((item) => {
            if (item.trend) item.trend = [...new Set([defaultTrend, ...item.trend])];
            if (item.sgfName !== "Others") return item;
          })
        );
        setSgfName(res.data.data[0].id);
        setTechnologyData(res.data.data[0].trend);
        setSelectedTechnology(res.data.data[0].trend[0].trendId);
        setSgfData({
          trend_parent_id: res.data.data[0].sector[0].sectorId,
          trend_parent_name: res.data.data[0].sector[0].sectorName,
          trend_id: res.data.data[0].id,
          trend_name: res.data.data[0].sgfName,
          trend_is_sgf: 1,
          trend_sub_root: "",
          trend_root: res.data.data[0].sector[0].sectorId,
        });
      })
      .catch((err) => {
        dispatch(hideLoader());
        console.error("err fetchTrend :: ", err);
      });
  };
  /*
        @Description : Fetch all taxonomy for selected trend
        @param 
        trendSubRoot : Trend Id
        taggingData : Content taxonomy tagged data [ Use for mark as default check ]
    */
  const fetchTaxonomy = (trendParentId, updatedSgfName) => {
    taggingData = [...new Set(contentTaggedIds)];
    let trendIds = [];
    let filters = [["trendSubRoot", "eq", trendParentId]];
    if (trendParentId === "0") {
      trendParentId = selectedSgfIndustry;
      let trends = sgfTrendData.filter((v) => {
        if (v.id == (updatedSgfName || sgfName)) return v;
      });

      trendIds = trends?.[0]?.trend
        .map((v) => {
          if (v.trendId !== "0") return v.trendId;
        })
        .filter(Boolean);

      filters = [
        [
          "or",
          [
            ["trendSubRoot", "in", trendIds],
            ["_id", "in", trendIds],
          ],
        ],
      ];
    }

    dispatch(showLoader());
    fetchTaxonomyTrend({
      fields: ["id", "trendName", "trendParentId", "trendParentName", "trendLevel", "trendSubRoot"],
      filters: filters,
      limit: import.meta.env.VITE_TAXONOMY_LIMIT || 10000,
    })
      .then((res) => {
        dispatch(hideLoader());
        let { data = [] } = res.data;
        let checkNode = [];
        if (data.length) {
          const treeJosnArray = buildTreeJson(data, trendParentId);
          data.forEach((element) => {
            if (taggingData.includes(element.id)) {
              checkNode.push(element.id);
              element["isChecked"] = true;
            }
          });
          setChecked(checkNode);
          setTaxonomy(treeJosnArray);
          setRightTreeCollapse(true);
        } else {
          setTaxonomy([]);
          setRightTreeCollapse(false);
          setRightTreeChecked([]);
          setChecked([]);
        }
      })
      .catch((err) => {
        dispatch(hideLoader());
        console.error("err fetchTaxonomy :: ", err);
      });
  };

  /*
        @Description : Build tree data
        @param : 
        data : Trend taxonomy data
        parentId : technology id
    */
  const buildTreeJson = (data, parentId) => {
    const result = [];
    data.forEach((element) => {
      element["value"] = element.id;
      element["label"] = element.trendName;
      element["level"] = element.trendLevel;
      element["showCheckbox"] = true;
      if (element["trendParentId"] == parentId) {
        const children = buildTreeJson(data, element["id"]);
        if (children.length > 0) {
          element["children"] = children;
        }
        result.push(element);
      }
    });
    // Sort the result array alphabetically by label
    result.sort((a, b) => a.label?.localeCompare(b.label));
    return result;
  };
  /*
       @Description : Build tree data for tagged values
       @param : 
       data : taxonomy data
       parentId :industry id
   */
  const buildSelectedTreeJson = (data, parentId = 0) => {
    const result = [];
    let count = 0;
    let fetchedSeq = [];
    data.forEach((element) => {
      if (element?.trendSortOrder) {
        fetchedSeq.push({ trendId: element.id, trendName: element.trendName, trendSortOrder: element.trendSortOrder });
      }
      element["id"] = element.trendId;
      element["value"] = element.trendId;
      element["label"] = element.trendName;
      if (element?.trendSortOrder === 1) {
        element["icon"] = "sortOne";
        count++;
      }
      if (element?.trendSortOrder === 2) {
        element["icon"] = "sortTwo";
        count++;
      }
      if (element?.trendSortOrder === 3) {
        element["icon"] = "sortThree";
        count++;
      }
      if (element?.trendSgfParentId) {
        element["parentId"] = element.trendSgfParentId;
      } else {
        element["parentId"] = element.trendParentId;
      }
      if (element["trendParentId"] == parentId) {
        const children = buildSelectedTreeJson(data, element["trendId"]);
        if (children.length > 0) {
          element["children"] = children;
        }
        result.push(element);
      }
    });
    fetchedSeq.sort((a, b) => a.trendSortOrder - b.trendSortOrder);
    setRightTreeCheckedSequence(fetchedSeq);
    setRightTreeCheckedSequenceTag(fetchedSeq);
    setSequenceCount(count);
    return result;
  };

  const buildSelectedSgfTreeJson = (data, parentId = 0) => {
    const result = [];
    data.forEach((element) => {
      let siblings = element?.children;
      if (siblings) {
        let sgfChilds = siblings.filter((item) => {
          if (item.trendIsSgf === 1) return item.trendId;
        });
        let sgfChild = sgfChilds.map((item) => {
          let sgfChildren = siblings.filter((v) => {
            if (item.trendId == v.trendSgfParentId) {
              return v;
            }
          });
          item["children"] = sgfChildren;
          return item;
        });
        element.children = sgfChild;
      }
      result.push(element);
    });
    return result;
  };
  /*
      @Description : Make bridge to send callback
      @param : 
      selectedTech : selected technology
      checkValues : Checked taxonomy
  */
  const handleSaveBridge = (selectedTech, checkValues) => {
    if (selectedTech == "0") {
      // selectedTech =

      let trends = sgfTrendData.filter((v) => {
        if (v.id == sgfName) return v;
      });
      selectedTech = trends[0].trend.map((v) => {
        if (v.trendId !== "0") return v.trendId;
      });
      selectedTech = selectedTech.filter((item) => item !== "0" && item !== null && item !== undefined);
    } else {
      selectedTech = selectedTech.split(",");
    }
    if (checkValues.length) props.handleSave(selectedTech, checkValues, fetchContentTagging, sgfData);
    else dispatch(showMessage("Please select taxonomy..!"));
  };

  const deleteTrend = () => {
    if (props.isDelete) {
      dispatch(
        showAlertBox({
          okCallback: async () => {
            props.deleteTrend([...new Set(rightTreeChecked)], fetchContentTagging);
          },
          okText: "Delete",
          cancelText: "Cancel",
          content: "Do you want to remove taxonomy tagging ? ",
          title: "dialogAlertCss",
        })
      );
    } else {
      setShowModalDelete(true);
    }
  };

  const rightTreeOnChecked = (checkedLeft) => {
    let result = [];
    let tagSeq = [];
    checkedLeft.forEach(function (a, i) {
      result.push(a.id);
      tagSeq.push({ trendId: a.id, trendName: a.trendName, trendSortOrder: i + 1 });
      if (Array.isArray(a.children)) {
        result = result.concat(rightTreeOnChecked(a.children));
      }
    });
    setRightTreeChecked(result);
    setRightTreeCheckedSequence(tagSeq);
    return result;
  };

  const searchContentTag = async (searchText) => {
    const response = await fetchContentTags({
      fields: ["tagName"],
      filters: [["tagName", "cn", searchText]],
      sort: "tagName:asc",
      limit: 10,
    });
    if (response?.data?.data) {
      const resData = response?.data?.data?.map((v) => {
        return { label: v.tagName, value: v.tagName };
      });
      if (resData) {
        setContentTagsDataSource(resData);
      }
    }
  };

  /*
       @Description : Fetch tags for respective content 
       1. fetchContentTags received from parent component as a props
   */
  const fetchTags = () => {
    const test = props.fetchTags(props.contentId);
    test
      .then((res) => {
        if (props.contentModule === "Deliverable") formik.setValues({ contentTag: res.data.data.deliverableContentTags });
        if (props.contentModule === "News") formik.setValues({ contentTag: res.data.data.newsContentTags });
        if (props.contentModule === "Company") formik.setValues({ contentTag: res.data.data.companyContentTags });
        if (props.contentModule === "Product") formik.setValues({ contentTag: res.data.data.productContentTags });
        if (props.contentModule === "Video") formik.setValues({ contentTag: res.data.data.videoContentTags });
      })
      .catch((err) => {
        console.error("err fetchProductTaxonomy :: ", err);
      });
  };

  const saveTags = () => {
    dispatch(showLoader());
    let TagsData;
    let TagsDataForDB = formik.values.contentTag;

    if (props.contentModule === "Deliverable") TagsData = { deliverableContentTags: formik.values.contentTag };
    if (props.contentModule === "News") TagsData = { newsContentTags: formik.values.contentTag };
    if (props.contentModule === "Company") TagsData = { companyContentTags: formik.values.contentTag };
    if (props.contentModule === "Product") TagsData = { productContentTags: formik.values.contentTag };
    if (props.contentModule === "Video") TagsData = { videoContentTags: formik.values.contentTag };

    let saveTagsData = new FormData();

    for (let uKey in TagsData) {
      if (typeof TagsData[uKey] !== "object") {
        if (TagsData[uKey]) saveTagsData.append(uKey, TagsData[uKey]);
      } else {
        if (TagsData[uKey]) saveTagsData.append(uKey, JSON.stringify(TagsData[uKey]));
      }
    }
    let response, response2;

    response = props.saveTags(props.contentId, saveTagsData);

    response
      .then((response) => {
        dispatch(hideLoader());
      })
      .catch((e) => {
        dispatch(hideLoader());
        if (e?.data?.message) {
          dispatch(actionError(e.data.message));
        } else {
          dispatch(actionError("Something Went Wrong...!"));
        }
      });

    if (TagsDataForDB) {
      response2 = addContentTags(TagsDataForDB);

      response2
        .then((res) => {})
        .catch((e) => {
          if (e?.data?.message) {
            dispatch(actionError(e.data.message));
          } else {
            dispatch(actionError("Something Went Wrong...!"));
          }
        });
    }
  };

  const showInProduct = () => {
    if (rightTreeCheckedSequence.length > 3) {
      dispatch(actionError("You can select maximum 3 nodes."));
    } else {
      setRightTreeCheckedSequenceTag(rightTreeCheckedSequence);
      setShow(true);
    }
  };

  const handleSaveTagSequence = (sortedTags) => {
    props.handleSaveTagSequence(sortedTags, fetchContentTagging);
    setShow(false);
  };

  useEffect(() => {
    if (formik.values.contentTag) {
      setTagsLength(formik.values.contentTag.length);
    }
  }, [formik.values.contentTag]);

  useEffect(() => {
    fetchContentTagging(true);
    if (
      props.contentModule === "Deliverable" ||
      props.contentModule === "News" ||
      props.contentModule === "Company" ||
      props.contentModule === "Product" ||
      props.contentModule === "Video"
    )
      fetchTags();
  }, []);
  useEffect(() => {
    if (reRender && selectedTechnology) {
      fetchTaxonomy(selectedTechnology, sgfName);
      setReRender(false);
    }
  }, [reRender, contentTaggedIds]);

  useEffect(() => {
    if (!selectedSgfIndustry) fetchIndustry();
  }, []);

  useEffect(() => {
    if (props.saveTreeData) {
      handleSaveBridge(selectedTechnology, leftTreeChecked);
    }
  }, [props.saveTreeData]);
  useEffect(() => {
    if (props?.submitStatus) {
      let selectedTech;
      if (selectedTechnology == "0") {
        let trends = sgfTrendData.filter((v) => {
          if (v.id == sgfName) return v;
        });
        selectedTech = trends[0].trend.map((v) => {
          if (v.trendId !== "0" && leftTreeChecked.includes(v.trendId)) return v.trendId;
        });
        selectedTech = selectedTech.filter((item) => item !== "0" && item !== null && item !== undefined);
      } else {
        selectedTech = selectedTechnology?.split(",");
      }
      props.handleSubmit(selectedTech, leftTreeChecked, fetchContentTagging, sgfData, selectedTech.length ? selectedTech[0] : contentTaggedIds[0]);
    }
  }, [props.submitStatus]);

  useEffect(() => {
    if (props.saveTrendTreeData) {
      props.setSaveTrendTreeData(false);
      saveTags();
    }
  }, [props.saveTrendTreeData]);

  return (
    <>
      <div
        className="row trendTreeLayout"
        style={{
          margin: "1rem",
        }}
      >
        <div className="col-md-6 TaxonomyNewTabLeftContent px-3">
          {sgfName && (
            <div
              className="row"
              style={{
                justifyContent: "space-between",
                paddingBottom: "0.5em",
                paddingTop: "1em",
                marginLeft: "0em",
                marginRight: "0em",
              }}
            >
              <div className="col-4" style={{ paddingLeft: "0px" }}>
                {industry?.length && selectedSgfIndustry && (
                  <DropDownListComponent
                    value={selectedSgfIndustry}
                    change={(value) => {
                      setSgfData({
                        ...sgfData,
                        trend_parent_id: value?.itemData.id,
                        trend_root: value?.itemData.id,
                      });
                      setSelectedSgfIndustry(value.itemData.id);
                      fetchTrend(value.itemData.id);
                      setSgfTrendData([]);
                      setTechnologyData([]);
                      setTaxonomy([]);
                    }}
                    cssClass={"customCss e-outline"}
                    floatLabelType="Auto"
                    popupHeight="250px"
                    dataSource={industry}
                    fields={{ text: "trendName", value: "id" }}
                    placeholder="Select Industry"
                  />
                )}
              </div>

              <div className="col-4">
                {sgfTrendData?.length > 0 && (
                  <DropDownListComponent
                    value={sgfName}
                    change={(value) => {
                      setSgfData({
                        ...sgfData,
                        trend_id: value?.value,
                        trend_name: value?.itemData?.sgfName,
                      });
                      setSgfName(value?.itemData.id);
                      fetchSgfTrend(value?.itemData.sgfName);
                      setTechnologyData(value?.itemData.trend);
                      setSelectedTechnology(value?.itemData.trend[0].trendId);
                      fetchTaxonomy(value?.itemData.trend[0].trendId, value?.itemData.id);
                      setIndustry(industry);
                    }}
                    cssClass={"customCss e-outline"}
                    floatLabelType="Auto"
                    popupHeight="250px"
                    dataSource={sgfTrendData}
                    fields={{ text: "sgfName", value: "id" }}
                    placeholder="Select SGF"
                  />
                )}
              </div>

              <div className="col-4" style={{ paddingRight: "0px" }}>
                {technologyData.length > 0 && (
                  <DropDownListComponent
                    value={selectedTechnology}
                    cssClass={"customCss e-outline"}
                    floatLabelType="Auto"
                    popupHeight="250px"
                    dataSource={technologyData}
                    fields={{ text: "trendName", value: "trendId" }}
                    placeholder="Select Trend"
                    change={(value) => {
                      setSelectedTechnology(value?.itemData?.trendId);
                      fetchTaxonomy(value?.itemData?.trendId, sgfName);
                      setIndustry(industry);
                    }}
                  />
                )}
              </div>
            </div>
          )}
          <div className="row mx-0" style={{ paddingTop: ".25rem" }}>
            <div className="col-12 p-0">
              {taxonomy?.length > 0 && (
                <div className="float-right" style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
                  {props?.accessPermissionTaxonomy && (
                    <button
                      type="button"
                      className={`btn px-3 ${leftTreeChecked.length > 0 ? "btn-primary" : "btn-primary-disabled"}`}
                      onClick={() => {
                        console.log("add");
                        setLeftTreeCollapse(true);
                        if (props?.isDelete) {
                          props.setSaveTreeData("save");
                        } else {
                          console.log("add if", props?.isDelete);
                          setShowModalAdd(true);
                        }
                      }}
                    >
                      Add
                    </button>
                  )}
                  {leftTreeCollapse ? (
                    <span
                      className="expandButton btn px-3"
                      style={{ border: "1px solid #F94F5E", color: "#F94F5E" }}
                      onClick={() => setLeftTreeCollapse(false)}
                    >
                      Expand All{" "}
                    </span>
                  ) : (
                    <span
                      className="expandButton btn px-3"
                      style={{ border: "1px solid #F94F5E", color: "#F94F5E" }}
                      onClick={() => setLeftTreeCollapse(true)}
                    >
                      Collapse All
                    </span>
                  )}
                </div>
              )}
              <div>
                {taxonomy.length > 0 ? (
                  <SortableTree
                    treeData={taxonomy}
                    onClick={(checkedRight) => {
                      let ids = checkedRight.map((item) => {
                        return item.id;
                      });
                      setLeftTreeChecked(ids);
                    }}
                    treeCollapse={leftTreeCollapse}
                    checked={checked}
                    parentId={"trendParentId"}
                    enablePersistance={true}
                    expanded={checked}
                    hieghtClass={"custom-tagging-tree-container-wrapper"}
                    newSearchbox={true}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6 TaxonomyNewTabRightContent px-0">
          <div className="row" style={{ paddingTop: "1rem", paddingBottom: ".5rem", marginLeft: "1rem", marginRight: "1rem" }}>
            <div className="">
              <h6 className="font-weight-bold">Trend</h6>
            </div>
            {tagging?.length > 0 && (
              <div className="ml-auto" style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {props?.accessActionTaxonomyShowInProduct && (
                  <button
                    type="button"
                    className={`btn px-3 ${rightTreeChecked.length > 0 || sequenceCount > 0 ? "btn-primary" : "btn-primary-disabled"}`}
                    onClick={() => showInProduct()}
                  >
                    {`Show in Product (${sequenceCount})`}
                  </button>
                )}
                {props?.accessPermissionTaxonomyDelete && (
                  <button
                    type="button"
                    className={`btn px-3 ${rightTreeChecked.length > 0 ? "btn-primary" : "btn-primary-disabled"}`}
                    onClick={() => deleteTrend()}
                  >
                    Delete
                  </button>
                )}

                {rightTreeCollapse ? (
                  <span
                    className="expandButton btn px-3"
                    style={{ border: "1px solid #F94F5E", color: "#F94F5E" }}
                    onClick={() => setRightTreeCollapse(false)}
                  >
                    Expand All{" "}
                  </span>
                ) : (
                  <span
                    className="expandButton btn px-3"
                    style={{ border: "1px solid #F94F5E", color: "#F94F5E" }}
                    onClick={() => setRightTreeCollapse(true)}
                  >
                    Collapse All
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="row" style={{ marginLeft: "0px", marginRight: "0px" }}>
            <div className="col-12 px-0" style={{ paddingTop: "0.5em" }}>
              {tagging?.length > 0 ? (
                <>
                  <div className="px-3">
                    <SortableTree
                      treeData={tagging}
                      onClick={rightTreeOnChecked}
                      treeCollapse={rightTreeCollapse}
                      parentId={"parentId"}
                      flatData={true}
                      hieghtClass={
                        ["News", "Company", "Deliverable", "Product", "Video"].includes(props.contentModule)
                          ? "custom-tagging-tree-container-wrapper-with-tags"
                          : "custom-tagging-tree-container-wrapper"
                      }
                      icon={true}
                      newSearchbox={true}
                    />
                  </div>

                  {["News", "Company", "Deliverable", "Product", "Video"].includes(props.contentModule) ? (
                    <div className="TaxonomyTagsMultiselect px-3" style={{ borderTop: "1px solid lightgrey" }}>
                      <div className="TaxonomyTags mx-0"></div>
                      <div className="row mx-0" style={{ alignItems: "center", justifyContent: "space-between" }}>
                        <h6 className="font-weight-bold" style={{ paddingBottom: "0.5em", paddingTop: "1.5em" }}>
                          Tags ({tagsLength})
                        </h6>
                      </div>
                      <div className="col-md-12 px-0 pt-2">
                        <MultiSelect
                          formik={formik}
                          mode="Box"
                          allowCustomValue={true}
                          allowFiltering={true}
                          required=""
                          // placeholder="Content Tag"
                          id="contentTag"
                          name="contentTag"
                          value={formik.values.contentTag}
                          fields={{ text: "label", value: "value" }}
                          filtering={(e) => {
                            searchContentTag(e.text);
                          }}
                          changeOnBlur={false}
                          dataSource={contentTagsDataSource}
                          floatLabelType={undefined}
                        />
                      </div>{" "}
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <div className="text-center px-3" style={{ minHeight: "42vh", maxHeight: "42vh", borderTop: "1px solid lightgrey" }}>
                    <img src={noTaxonomyImage} alt="No Tagging" width="200" height="200" />
                    <div className="info-container">
                      <p>Select Tags to create Taxonomy</p>
                    </div>
                  </div>
                  {["News", "Company", "Deliverable", "Product", "Video"].includes(props.contentModule) ? (
                    <div className="TaxonomyTagsMultiselect px-3">
                      <div className="TaxonomyTags mx-0"></div>
                      <div className="row mx-0">
                        <h6 className="font-weight-bold" style={{ paddingBottom: "0.5em", paddingTop: "1.5em" }}>
                          Tags ({tagsLength})
                        </h6>
                      </div>
                      <div className="col-md-12 px-0 pt-2">
                        <MultiSelect
                          formik={formik}
                          mode="Box"
                          allowCustomValue={true}
                          allowFiltering={true}
                          required=""
                          // placeholder=""
                          id="contentTag"
                          name="contentTag"
                          value={formik.values.contentTag}
                          fields={{ text: "label", value: "value" }}
                          filtering={(e) => {
                            searchContentTag(e.text);
                          }}
                          changeOnBlur={false}
                          dataSource={contentTagsDataSource}
                          floatLabelType={undefined}
                        />
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
        {/* <div className="col-md-6" style={{ background: "#FFFFFF", "border": "1px solid lightgrey", "border-radius": "4px" }}> */}
      </div>

      {show && <SideBar show={show} setShow={setShow} selectedTaxonomy={rightTreeCheckedSequenceTag} handleSaveSequence={handleSaveTagSequence} />}

      <Modal show={showModalAdd} backdrop="static" className="modal fade lg" size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Body>
          <div className="row">
            <div className="col-md-12 mb-3" style={{ marginLeft: "24px !important" }}>
              Since this company has products, you can not add taxonomy on company level. You need to add taxonomy in product.
            </div>
          </div>
        </Modal.Body>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline-dark cy-btn" onClick={() => setShowModalAdd(!showModalAdd)}>
            Ok
          </button>
        </div>
      </Modal>

      <Modal show={showModalDelete} backdrop="static" className="modal fade lg" size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Body>
          <div className="row">
            <div className="col-md-12 mb-3" style={{ marginLeft: "24px !important" }}>
              Since this company has products, you can not delete taxonomy from company. You need to delete taxonomy from product.
            </div>
          </div>
        </Modal.Body>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline-dark cy-btn" onClick={() => setShowModalDelete(!showModalDelete)}>
            Ok
          </button>
        </div>
      </Modal>
    </>
  );
};
export default Taxonomy;
Taxonomy.propTypes = {
  fetchContentTagging: PropTypes.func.isRequired,
  loadOtherTagInfo: PropTypes.func,
  handleSave: PropTypes.func.isRequired,
  saveTreeData: PropTypes.bool,
  contentId: PropTypes.string.isRequired,
  saveTrendTreeData: PropTypes.bool,
  setSaveTrendTreeData: PropTypes.func,
  contentModule: PropTypes.string,
  isDelete: PropTypes.bool,
};
